import { Entity, FloorFallView } from "game-common/models"
import { floorFallOffsetAt } from "game-common/floor_fall"
import { STOREY_RISE_PX } from "game-common/room/ember/two_storey_house"
import { predictedFallAirborne, predictedFallCurrent, predictedFallOffset, predictedFallElapsedMs, predictedFallPending } from "../../../../predicted_fall"

interface FallingSprite {
    x?: number; y: number; alpha: number
    scale?: { x: number; y: number; set(x: number, y: number): void }
}
interface FallPlayback { view: FloorFallView; receivedAt: number; alpha: number; sx: number; sy: number; outgoing: boolean }
const playback = new WeakMap<FallingSprite, FallPlayback>()
interface ArrivalPlayback { id: string; startedAt?: number; durationMs: number; complete: boolean }
const arrivals = new WeakMap<FallingSprite, ArrivalPlayback>()
// The camera has the entity, not its sprite, and rides the same arrival the body plays.
const bodyArrivals = new WeakMap<Entity, ArrivalPlayback>()
const trajectories = new WeakMap<Entity, { view: FloorFallView; elapsedMs: number; receivedAt: number }>()
/**
 * A body this long in the air goes limp: it takes the downed pose mid-fall and lands in it. On the
 * server's gravity a drop lasts 240ms * sqrt(floors), so this is a fall through five storeys or more.
 */
export const FLOOR_FALL_DOWNED_AFTER_MS = 500
// The wire drops the fall for a message or two at every floor crossing (its view names the room
// below before the body has been moved there) and again just before "landed". A limp body held
// through that gap does not stand up and collapse again at each floor.
const KNOCKDOWN_GAP_MS = 200
// The fall that laid this sprite's body down, kept per sprite like arrivals. It hands over to
// "landed", which holds the same pose until the recovery ends.
const knockedDown = new WeakMap<FallingSprite, { id: string; seenAt: number }>()

/** A body in the air, as the renderer draws it: dropping between floors or off a ladder (LpcComposite flings its arms up). */
export interface Airborne {
    airborne?: boolean
}

export const isAirborne = (entity: Entity): boolean => (entity as Entity & Airborne).airborne === true

// The entity each render view below was made from. A view is a fresh object every frame, so state
// kept per entity -- the fall's clock -- is kept against where it came from, or a body drawn limp or
// airborne would restart its fall every frame.
const viewOrigins = new WeakMap<Entity, Entity>()
const originOf = (entity: Entity): Entity => viewOrigins.get(entity) ?? entity
/** This frame's view of `entity` with `fields` drawn differently; the entity itself is never touched. */
const renderView = (entity: Entity, fields: Partial<Entity> & Airborne): Entity => {
    const view: Entity = Object.assign(Object.create(entity), fields)
    viewOrigins.set(view, originOf(entity))
    return view
}

/**
 * The fall a body is part way down several storeys on, through the message or two at each floor it passes that the
 * wire leaves it out of: it carries on down the curve it was on. Drawn standing on that floor instead, it popped up
 * to it for a frame at every storey it fell past.
 */
const crossing = (entity: Entity, now: number): FloorFallView | undefined => {
    if (entity.floorFall) return undefined
    const state = trajectories.get(originOf(entity))
    return state && state.view.phase === "falling" && state.view.roomId === entity.roomId &&
        now - state.receivedAt <= KNOCKDOWN_GAP_MS ? state.view : undefined
}

/** Share one monotonic clock between the body and camera; delayed snapshots never rewind it. */
export const floorFallOffsetForRendering = (entity: Entity, now = Date.now()): number => {
    const view = entity.floorFall ?? crossing(entity, now)
    // Room crossings briefly omit the fall. Keep its clock so the next delayed snapshot
    // cannot restart airborne time; a different fall id replaces it in trajectoryElapsedMs.
    if (!view?.trajectory) return 0
    if (view.phase === "landed") return 0
    return floorFallOffsetAt(view, trajectoryElapsedMs(entity, view, now))
}

const trajectoryElapsedMs = (entity: Entity, view: FloorFallView, now: number): number => {
    let state = trajectories.get(originOf(entity))
    const wireElapsedMs = view.trajectory.endElapsedMs - view.remainingMs
    if (!state || state.view.id !== view.id) {
        state = { view, elapsedMs: predictedElapsedMs(entity, view, now) ?? wireElapsedMs, receivedAt: now }
        trajectories.set(originOf(entity), state)
    } else if (state.view !== view) {
        state.elapsedMs = Math.max(wireElapsedMs, state.elapsedMs + now - state.receivedAt)
        state.receivedAt = now
        state.view = view
    }
    return state.elapsedMs + now - state.receivedAt
}

/**
 * Where on its curve the server's fall is -- a leap's, or a step's down several storeys -- when this client already
 * drew the body going into it (predicted_fall.ts): as far as that drop has got, not back at the top a round trip later.
 */
const predictedElapsedMs = (entity: Entity, view: FloorFallView, now: number): number | undefined => {
    const origin = originOf(entity)
    const fall = origin.predictedFall
    if (!view.trajectory || !view.fromAbove || !fall || !!view.leap !== !!fall.view.leap || !predictedFallCurrent(fall, now))
        return undefined
    origin.predictedFall = undefined
    // A step this client took for one storey that the server finds open below too (at a ledge's corner its copy of
    // the body is a step further on) is the same fall from rest: picked up from when it began, not from the top.
    const predicted = fall.view.trajectory ? predictedFallElapsedMs(fall, now) : now - fall.startedAt
    return Math.max(view.trajectory.endElapsedMs - view.remainingMs, predicted)
}

/**
 * When the server's one-storey arrival began, as this client's own body went over the edge it
 * stepped off (predicted_fall.ts): the drop already under way, not started again a round trip later.
 */
const predictedArrivalStart = (entity: Entity, view: FloorFallView, now: number): number | undefined => {
    const origin = originOf(entity)
    const fall = origin.predictedFall
    // (Taken for deeper than the server finds it, the fall still began when the step did.)
    if (view.leap || !view.fromAbove || !fall || fall.view.leap || !predictedFallCurrent(fall, now)) return undefined
    origin.predictedFall = undefined
    return fall.startedAt
}

/** Time spent in the air: stops where the body meets the ground it is falling towards (see floorFallOffsetAt). */
const airborneMs = (view: FloorFallView, elapsedMs: number): number => {
    const { gravity, endElapsedMs, totalDistance } = view.trajectory
    return Math.min(elapsedMs, view.phase === "falling" && totalDistance !== undefined
        ? Math.sqrt(2 * totalDistance / gravity) : endElapsedMs)
}

/**
 * Whether this client's own leap is still in the air on the way down, on the clock the body is drawn by (picked up
 * from the drop this client started itself): it carries the speed it took off with until then. The server applies
 * those moves a one-way trip after they were made, and its fall started that much later too, so it has them over the
 * same part of the drop. Carried on until the server's messages said it was down, a round trip late, the body slid on
 * past where the server stopped it and was put back.
 */
export const leapFallAirborne = (entity: Entity, now = Date.now()): boolean => {
    const view = entity.floorFall ?? crossing(entity, now)
    if (!view?.leap || view.phase === "landed") return false
    return !view.trajectory || trajectoryElapsedMs(entity, view, now) < airborneMs(view, Infinity)
}

// LpcComposite lays a `downed` body down; the authoritative entity is never touched.
const limp = (view: Entity, sprite: FallingSprite): Entity =>
    knockedDown.has(sprite) ? renderView(view, { downed: true }) : view

// When this sprite's body was last seen part way down a chained fall, with another floor below it.
const midFall = new WeakMap<FallingSprite, number>()

/**
 * Whether the body is drawn in the air: the drop it is shown taking says so (the wire's, or the
 * local arrival's), and so does a destination still loading -- it is between floors. A fall on the
 * wire's clock touches down when it reaches the floor it is falling to, however late the server's
 * `landed` gets here. Seen part way down a chain of floors, the body stays in the air through the
 * message or two the wire drops at each crossing.
 */
const inTheAir = (entity: Entity, view: Entity, sprite: FallingSprite, now: number, roomId: string | undefined): boolean => {
    const wire = entity.floorFall
    const pending = !!wire?.fromAbove && !!wire.roomId && wire.roomId !== roomId && wire.phase !== "landed"
    const shown = view.floorFall
    const dropping = !!shown && shown.phase !== "landed" &&
        // (Never yet on the ground, the time in the air is the whole fall: when it touches down.)
        (!shown.trajectory || trajectoryElapsedMs(entity, shown, now) < airborneMs(shown, Infinity))
    if (pending || dropping) {
        if (wire?.phase === "falling") midFall.set(sprite, now)
        else midFall.delete(sprite)
        return true
    }
    const seen = midFall.get(sprite)
    if (!wire && seen !== undefined && now - seen <= KNOCKDOWN_GAP_MS) return true
    midFall.delete(sprite)
    return false
}

/** A room load may consume the wire arrival phase. Play it once after the curtain opens. */
export const floorFallForRendering = (entity: Entity, sprite: FallingSprite, visible = true, now = Date.now(), roomId = entity.roomId): Entity => {
    const view = fallView(entity, sprite, visible, now, roomId)
    // Stepped off an edge, and dropping on this client before the server's word (a leap keeps its jump's pose).
    const predicted = !entity.floorFall ? predictedFallPending(entity, now) : undefined
    if (predicted && !predicted.view.leap && predictedFallAirborne(predicted, now)) return renderView(view, { airborne: true })
    if (!inTheAir(entity, view, sprite, now, roomId)) return view
    return renderView(view, { airborne: true })
}

const fallView = (entity: Entity, sprite: FallingSprite, visible: boolean, now: number, roomId: string | undefined): Entity => {
    const view = entity.floorFall
    const knock = knockedDown.get(sprite)
    if (knock && (view ? view.id !== knock.id || view.phase === "landed" : now - knock.seenAt > KNOCKDOWN_GAP_MS)) {
        knockedDown.delete(sprite)
    } else if (knock && view) knock.seenAt = now
    // Advance the shared airborne clock through short gaps at floor crossings too.
    // With network delay the 500ms threshold can fall entirely between two snapshots.
    const previous = trajectories.get(entity)
    const airborne = view?.trajectory ? view : !view && previous && now - previous.receivedAt <= KNOCKDOWN_GAP_MS
        ? previous.view : undefined
    if (airborne?.fromAbove && airborne.phase !== "landed" && !knockedDown.has(sprite) &&
        airborneMs(airborne, trajectoryElapsedMs(entity, airborne, now)) > FLOOR_FALL_DOWNED_AFTER_MS) {
        knockedDown.set(sprite, { id: airborne.id, seenAt: now })
    }
    // Messages and the active map can switch rooms on different frames. Do not
    // lift the body or start arrival playback while its destination is pending.
    if (view?.fromAbove && view.roomId && view.roomId !== roomId) {
        return limp(renderView(entity, { floorFall: undefined }), sprite)
    }
    if (!view?.fromAbove) { arrivals.delete(sprite); return limp(entity, sprite) }
    // Chained floors use wire time, including late arrivals and intermediate rooms.
    // Replaying a local arrival would restart velocity on every room crossing.
    // Only these last long enough to go limp: a one-floor arrival is a fixed short drop.
    if (view.trajectory) {
        arrivals.delete(sprite)
        return limp(entity, sprite)
    }
    let arrival = arrivals.get(sprite)
    if (!arrival || arrival.id !== view.id) {
        arrival = { id: view.id, durationMs: view.arrivalDurationMs ?? 300, complete: false,
            startedAt: predictedArrivalStart(entity, view, now) }
        arrivals.set(sprite, arrival)
    }
    bodyArrivals.set(entity, arrival)
    if (arrival.complete) return landedArrival(entity)
    if (visible && arrival.startedAt === undefined) arrival.startedAt = now
    const durationMs = arrival.durationMs
    const elapsed = arrival.startedAt === undefined ? 0 : now - arrival.startedAt
    if (elapsed >= durationMs) { arrival.complete = true; return landedArrival(entity) }
    // Render a temporary view without changing the authoritative movement/recovery state.
    return renderView(entity, { floorFall: { ...view,
        id: arrival.startedAt === undefined ? `${view.id}:waiting` : view.id, phase: "arriving",
        durationMs, remainingMs: durationMs - elapsed } })
}


/**
 * Local playback usually ends a frame or two before the server's "arriving" phase does. Handing the
 * wire view back then re-applied its stale remaining time and lifted the body again for a frame.
 */
const landedArrival = (entity: Entity): Entity => entity.floorFall?.phase === "arriving"
    ? renderView(entity, { floorFall: undefined }) : entity

/**
 * A one-floor drop starts where the body stood on the storey above: that storey's rise (the server's `distance`).
 * A fixed 48px, left from when storeys were that tall, hopped the body 16px up as it stepped off a 32px storey.
 */
/** How far above where it lands a body coming down from the storey above is drawn, `progress` through its arrival. */
export const arrivalLift = (view: FloorFallView, progress: number): number =>
    -(view.distance > 0 ? view.distance : STOREY_RISE_PX) * (1 - progress * progress)

/**
 * The body's height for the camera to follow, as multi-floor falls already did. A one-floor drop
 * took 300ms while the floor-change pan took 600ms on its own curve, so the body sank below centre
 * and the camera kept drifting after it had landed.
 */
export const floorFallCameraOffset = (entity: Entity, roomId: string | undefined, now = Date.now()): number | undefined => {
    const view = entity.floorFall ?? crossing(entity, now)
    if (!view) {
        // This client's own step off an edge, dropping in the storey it is leaving before the server's word on it
        // (predicted_fall.ts): followed as the server's is, it meets that one when it comes. Left out, the camera held
        // still through the drop and lurched after the body at the hand-off. (A leap keeps its own camera, as below.)
        const predicted = predictedFallPending(entity, now)
        return predicted && !predicted.view.stairJump && predicted.roomId === roomId
            ? predictedFallOffset(predicted, entity.location, now).y : undefined
    }
    if (!view || view.stairJump || view.phase === "landed" || (view.fromAbove && view.roomId && view.roomId !== roomId)) return undefined
    if (view.trajectory) return floorFallOffsetForRendering(entity, now)
    if (!view.fromAbove) return undefined
    const arrival = bodyArrivals.get(entity)
    if (arrival?.id !== view.id) return arrivalLift(view, 0)
    if (arrival.complete) return undefined
    return arrivalLift(view, arrival.startedAt === undefined ? 0 : Math.min(1, (now - arrival.startedAt) / arrival.durationMs))
}

/** The departing body belongs below the active floor, above the lower companion room. */
export const floorFallZIndex = (entity: Entity, normal: number): number =>
    entity.floorFall?.phase === "falling" && !entity.floorFall.fromAbove ? -1 : normal


/** Run after normal placement. Remaining wire time is independent of client clock skew. */
export const applyFloorFall = (entity: Entity, sprite: FallingSprite, now = Date.now()): void => {
    let state = playback.get(sprite)
    if (state?.outgoing) { sprite.alpha = state.alpha; sprite.scale?.set(state.sx, state.sy) }
    const view = entity.floorFall
    // This client's own player gone over an edge, already on its way down in the storey it set out from (predicted_fall.ts).
    const predicted = !view ? predictedFallPending(entity, now) : undefined
    if (predicted) {
        playback.delete(sprite)
        const offset = predictedFallOffset(predicted, entity.location, now)
        if (sprite.x !== undefined) sprite.x += offset.x
        sprite.y += offset.y
        return
    }
    if (!view && crossing(entity, now)) {
        playback.delete(sprite)
        sprite.y += floorFallOffsetForRendering(entity, now)
        return
    }
    if (!view || view.phase === "landed") { playback.delete(sprite); return }
    if (!state) {
        state = { view, receivedAt: now, alpha: sprite.alpha, sx: sprite.scale?.x ?? 1, sy: sprite.scale?.y ?? 1, outgoing: false }
        playback.set(sprite, state)
    } else if (state.view !== view) {
        state.view = view; state.receivedAt = now
    }
    const incoming = view.phase === "arriving" || view.fromAbove
    state.outgoing = !incoming
    if (view.trajectory) {
        sprite.y += floorFallOffsetForRendering(entity, now)
        return
    }
    const progress = Math.max(0, Math.min(1, 1 - (view.remainingMs - (now - state.receivedAt)) / view.durationMs))
    if (incoming) {
        sprite.y += arrivalLift(view, progress)
        return
    }
    sprite.y += view.distance * progress * progress
    sprite.alpha = state.alpha * (1 - .65 * progress)
    sprite.scale?.set(state.sx * (1 - .18 * progress), state.sy * (1 - .18 * progress))
}
